import { useEffect, useState } from "react";
import { Briefcase, ChevronDown, Loader2, Plus, User } from "lucide-react";
import { cn } from "@slumbr/ui";
import {
  createAccountProfile,
  listAccountProfiles,
  useUserStore,
  type AccountProfileSummary,
  type ProfileType,
} from "@slumbr/core";

import { useProfileSwitcherStore } from "@/stores/profile-switcher-store";

function ProfileTypeIcon({ type, className }: { type?: ProfileType; className?: string }) {
  if (type === "business") return <Briefcase className={className} />;
  return <User className={className} />;
}

function ProfileAvatar({
  url,
  name,
  size = "md",
}: {
  url?: string;
  name: string;
  size?: "sm" | "md";
}) {
  const dims = size === "sm" ? "h-8 w-8 text-[10px]" : "h-9 w-9 text-xs";
  return (
    <span
      className={cn(
        "flex items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#c83050] to-[#5a1728] font-black text-white",
        dims
      )}
    >
      {url ? (
        <img src={url} alt={name} className="h-full w-full object-cover" />
      ) : (
        name.slice(0, 2).toUpperCase()
      )}
    </span>
  );
}

export function ProfileSwitcherMenu({ onClose }: { onClose?: () => void }) {
  const currentUser = useUserStore((s) => s.user);
  const profiles = useProfileSwitcherStore((s) => s.profiles);
  const activeProfileId = useProfileSwitcherStore((s) => s.activeProfileId);
  const setProfiles = useProfileSwitcherStore((s) => s.setProfiles);
  const setActiveProfileId = useProfileSwitcherStore((s) => s.setActiveProfileId);
  const upsertProfile = useProfileSwitcherStore((s) => s.upsertProfile);
  const [loading, setLoading] = useState(profiles.length === 0);
  const [expanded, setExpanded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState<{ displayName: string; username: string; profileType: ProfileType }>({
    displayName: "",
    username: "",
    profileType: "individual",
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(profiles.length === 0);
    listAccountProfiles()
      .then((response) => {
        if (cancelled) return;
        setProfiles(response.items);
        if (!activeProfileId && response.items.length > 0) {
          setActiveProfileId(response.items[0].userId);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function switchProfile(profile: AccountProfileSummary) {
    if (profile.userId === activeProfileId) {
      setExpanded(false);
      return;
    }
    setActiveProfileId(profile.userId);
    onClose?.();
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }

  async function submitNewProfile() {
    if (!draft.displayName.trim() || !draft.username.trim()) {
      setError("Pick a display name + username.");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const profile = await createAccountProfile({
        displayName: draft.displayName.trim(),
        username: draft.username.trim(),
        profileType: draft.profileType,
      });
      upsertProfile(profile);
      setActiveProfileId(profile.userId);
      setShowCreate(false);
      setDraft({ displayName: "", username: "", profileType: "individual" });
      onClose?.();
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create profile";
      setError(message);
    } finally {
      setCreating(false);
    }
  }

  const activeProfile =
    profiles.find((entry) => entry.userId === activeProfileId) ??
    profiles[0] ?? {
      userId: currentUser?.id ?? "",
      username: currentUser?.username ?? "",
      displayName: currentUser?.displayName ?? "Slumbr User",
      avatarUrl: currentUser?.avatar,
      profileType: "individual" as const,
    };
  const others = profiles.filter((entry) => entry.userId !== activeProfile.userId);

  return (
    <div className="p-2">
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className={cn(
          "flex w-full items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-left transition",
          "hover:border-[#ff6b8f]/30 hover:bg-white/[0.04]",
          expanded && "border-[#ff6b8f]/40 bg-white/[0.04]"
        )}
        aria-expanded={expanded}
      >
        <ProfileAvatar url={activeProfile.avatarUrl} name={activeProfile.displayName} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-black text-[#e7e9ea]">
            {activeProfile.displayName}
          </span>
          <span className="flex items-center gap-1 truncate text-[11px] text-[#71767b]">
            <ProfileTypeIcon type={activeProfile.profileType} className="h-3 w-3" />
            @{activeProfile.username}
          </span>
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-[#71767b] transition-transform",
            expanded && "rotate-180 text-[#ff6b8f]"
          )}
        />
      </button>

      {expanded && (
        <div className="mt-1.5 space-y-0.5">
          {loading ? (
            <div className="flex items-center gap-2 rounded-xl px-2 py-2.5 text-xs text-[#71767b]">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          ) : others.length === 0 && !showCreate ? (
            <p className="rounded-xl px-2 py-2 text-[11px] text-[#71767b]">
              You only have one profile.
            </p>
          ) : (
            others.map((profile) => (
              <button
                key={profile.userId}
                type="button"
                onClick={() => switchProfile(profile)}
                className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition hover:bg-white/[0.05]"
              >
                <ProfileAvatar url={profile.avatarUrl} name={profile.displayName} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-[#e7e9ea]">
                    {profile.displayName}
                  </span>
                  <span className="flex items-center gap-1 truncate text-[11px] text-[#71767b]">
                    <ProfileTypeIcon type={profile.profileType} className="h-3 w-3" />
                    @{profile.username}
                  </span>
                </span>
              </button>
            ))
          )}

          {!showCreate ? (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="mt-1 flex w-full items-center gap-2 rounded-xl border border-dashed border-white/[0.08] px-3 py-2 text-xs font-bold text-[#d7dbdc] transition hover:border-[#ff6b8f]/60 hover:text-[#ff6b8f]"
            >
              <Plus className="h-3.5 w-3.5" />
              New profile
            </button>
          ) : (
            <div className="mt-2 space-y-2 rounded-2xl border border-white/[0.08] bg-[#0b0d0f] p-3">
              <div className="grid grid-cols-2 gap-1">
                {(["individual", "business"] as ProfileType[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setDraft({ ...draft, profileType: option })}
                    className={cn(
                      "rounded-lg border px-2 py-1.5 text-[11px] font-bold capitalize transition",
                      draft.profileType === option
                        ? "border-[#ff6b8f] bg-[#ff6b8f]/10 text-[#ff8fac]"
                        : "border-white/[0.06] text-[#d7dbdc] hover:border-white/15"
                    )}
                  >
                    {option}
                  </button>
                ))}
              </div>
              <input
                className="w-full rounded-lg border border-white/[0.08] bg-[#111417] px-3 py-2 text-sm text-[#e7e9ea] outline-none transition focus:border-[#ff6b8f]/50"
                value={draft.displayName}
                onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
                placeholder="Display name"
              />
              <input
                className="w-full rounded-lg border border-white/[0.08] bg-[#111417] px-3 py-2 text-sm text-[#e7e9ea] outline-none transition focus:border-[#ff6b8f]/50"
                value={draft.username}
                onChange={(event) => setDraft({ ...draft, username: event.target.value.toLowerCase() })}
                placeholder="@handle"
              />
              {error && <p className="text-[11px] text-red-400">{error}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreate(false);
                    setError(null);
                  }}
                  className="rounded-lg px-3 py-1.5 text-[11px] font-bold text-[#71767b] transition hover:text-[#e7e9ea]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={creating}
                  onClick={() => void submitNewProfile()}
                  className="inline-flex items-center gap-1 rounded-lg bg-[#c83050] px-3 py-1.5 text-[11px] font-black text-white transition hover:bg-[#d63b5d] disabled:opacity-60"
                >
                  {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  Create
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
